/**
 * Le nom et l'emblème d'une matière, **au même endroit pour tous les écrans**.
 *
 * L'emoji est demandé par Hedi le 26 août 2026, pour le journal du soir et les
 * tableaux de bord. Il n'est pas décoratif : sur une liste de huit matières, un
 * enfant de douze ans retrouve la bonne ligne à l'emblème avant de lire le mot,
 * et un parent qui parcourt un tableau de bord au réveil aussi.
 *
 * **Une seule table, et c'est le point.** Le nom était déjà dupliqué entre la
 * coquille élève et ce que l'API renvoie ; y ajouter une seconde table d'emoji
 * par écran aurait garanti la divergence — c'est la « configuration parallèle à
 * maintenir » que `web-pwa` §4.2 refuse pour le clavier.
 *
 * **Chaque emoji est unique**, et un test le garde : deux matières qui
 * partageraient le même emblème le rendraient inutile, puisqu'il ne
 * distinguerait plus rien.
 */

/**
 * Nom, emblème, et forme de prose. L'identifiant est celui du référentiel.
 *
 * **Deux registres, parce que les écrans en ont besoin de deux.** `nom` titre une
 * carte — court, capitale initiale. `prose` se glisse dans une phrase — minuscule,
 * développé : « sciences de la vie et de la Terre » se lit mieux que « SVT » au
 * milieu d'un texte, et l'inverse est vrai en tête de carte.
 *
 * Les deux vivaient dans deux fichiers séparés jusqu'au 26 août — `config/wording`
 * pour la prose, `child/journee` pour les titres — et une matière ajoutée à l'un
 * manquait à l'autre sans que rien ne le dise.
 */
const MATIERES: Record<string, { nom: string; prose: string; emoji: string }> = {
  francais: { nom: "Français", prose: "français", emoji: "📖" },
  mathematiques: { nom: "Mathématiques", prose: "mathématiques", emoji: "➗" },
  "mathematiques-expertes": {
    nom: "Maths expertes",
    prose: "mathématiques expertes",
    emoji: "🧮",
  },
  anglais: { nom: "Anglais", prose: "anglais", emoji: "🇬🇧" },
  espagnol: { nom: "Espagnol", prose: "espagnol", emoji: "🇪🇸" },
  "histoire-geographie": {
    nom: "Histoire-géographie",
    prose: "histoire-géographie",
    emoji: "🗺️",
  },
  svt: { nom: "SVT", prose: "sciences de la vie et de la Terre", emoji: "🌱" },
  "physique-chimie": { nom: "Physique-chimie", prose: "physique-chimie", emoji: "🧪" },
  technologie: { nom: "Technologie", prose: "technologie", emoji: "🤖" },
  philosophie: { nom: "Philosophie", prose: "philosophie", emoji: "🦉" },
  "enseignement-scientifique": {
    nom: "Enseignement scientifique",
    prose: "enseignement scientifique",
    emoji: "🔬",
  },
  eps: { nom: "EPS", prose: "éducation physique et sportive", emoji: "🏃" },
  emc: { nom: "EMC", prose: "enseignement moral et civique", emoji: "⚖️" },
  "arts-plastiques": { nom: "Arts plastiques", prose: "arts plastiques", emoji: "🎨" },
  "education-musicale": {
    nom: "Éducation musicale",
    prose: "éducation musicale",
    emoji: "🎵",
  },
  latin: { nom: "Latin", prose: "latin", emoji: "🏛️" },
  "vie-de-classe": { nom: "Vie de classe", prose: "vie de classe", emoji: "🪑" },
  // **Pas une matière, et pourtant une ligne de la journée.** EcoleDirecte pose
  // « DST » sur deux heures du samedi de Mehdi ; l'écran du soir lui demande
  // comment s'est passée sa journée, et un devoir sur table en fait partie au
  // même titre qu'un cours. La prose reste au singulier : elle se glisse dans
  // « ton devoir sur table de samedi ».
  "devoir-sur-table": {
    nom: "Devoir sur table",
    prose: "devoir sur table",
    emoji: "✍️",
  },
};

/**
 * Les orthographes que l'API emploie encore, ramenées à l'identifiant canonique.
 *
 * `maths` et `maths-expertes` viennent d'avant la normalisation du référentiel ;
 * les effacer casserait des écrans, les dupliquer en ferait deux matières.
 */
const ALIAS: Record<string, string> = {
  maths: "mathematiques",
  "maths-expertes": "mathematiques-expertes",
};

function canonique(matiere: string): string {
  const brut = matiere.toLowerCase();
  return ALIAS[brut] ?? brut;
}

/**
 * L'emblème d'une matière inconnue.
 *
 * **Un point d'interrogation, délibérément.** Une matière ajoutée au référentiel
 * et pas ici doit se voir, pas se fondre : un emoji neutre et joli ferait passer
 * l'oubli pour une intention.
 */
const INCONNUE = "❔";

export function emoji(matiere: string): string {
  return MATIERES[canonique(matiere)]?.emoji ?? INCONNUE;
}

export function nommer(matiere: string): string {
  const connue = MATIERES[canonique(matiere)];
  if (connue) return connue.nom;
  // Un identifiant inconnu s'affiche lisiblement plutôt que brut : l'écran
  // vieillit mieux que la table.
  return matiere.replace(/-/g, " ").replace(/^./, (c) => c.toUpperCase());
}

/** « 📖 Français » — la forme que les écrans affichent. */
export function etiquette(matiere: string, libelle?: string): string {
  return `${emoji(matiere)} ${libelle?.trim() || nommer(matiere)}`;
}

/** La forme qui se glisse dans une phrase, minuscule et développée. */
export function enProse(matiere: string): string {
  return MATIERES[canonique(matiere)]?.prose ?? matiere;
}

/** Exporté pour le test d'unicité, et pour rien d'autre. */
export const MATIERES_CONNUES = MATIERES;

/**
 * Les matières où l'on écrit des formules dans sa prose.
 *
 * **Ce qui a rendu la question visible** : le champ de rédaction a été écrit
 * pour Mehdi, dont les démonstrations par récurrence sont de la prose contenant
 * de la notation. Servi tel quel à Baya sur un exercice de français, il lui
 * demandait « Écris ta démonstration » et lui proposait « insérer une formule ∑ »
 * — trouvé par Hedi en cliquant.
 *
 * Ce n'est pas un détail de vocabulaire. Une élève de douze ans qui lit peu et à
 * qui l'on demande une *démonstration* sur un exercice de lecture conclut qu'elle
 * n'a pas compris la consigne, pas que l'interface s'est trompée de matière.
 *
 * La liste est courte et se lit dans les deux sens : ailleurs, c'est de la prose
 * ordinaire, et le clavier système suffit.
 */
const AVEC_FORMULES = new Set([
  "mathematiques",
  "mathematiques-expertes",
  "physique-chimie",
  "enseignement-scientifique",
]);

export function demandeDesFormules(matiere: string): boolean {
  return AVEC_FORMULES.has(canonique(matiere));
}
